/**
 * ShortlistModal.tsx
 *
 * File-level:
 * - Compact shortlist modal that displays shortlisted profiles in a 3x2 grid (3 columns x 2 rows).
 * - Updated visuals: modal background uses a light pink tone and each profile card uses a thicker, colored border
 *   so profile-to-profile separation is visually more distinct.
 *
 * Notes:
 * - Keeps existing optimistic remove behavior, background avatar/profession resolution and undo.
 * - Uses fetchJSON and apiBase so runtime API base overrides continue to work.
 * - Ensures outline Buttons include bg-transparent as required by project rules.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { Check, RotateCcw, Eye, Trash2, X, User, Sparkles, Grid, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { fetchJSON, apiBase } from '@/utils/api';
import { Button } from '@/components/ui/button';

/**
 * ShortlistModalProps
 * @description Props for the ShortlistModal component
 */
interface ShortlistModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Logged-in profile id (owner) */
  profileId: string | null;
}

/**
 * ShortlistRow
 * @description Single shortlist entry
 */
interface ShortlistRow {
  profileId: string;
  firstName?: string;
  lastName?: string;
  profession?: string;
  shortlistedAt?: string;
  avatarUrl?: string;
}

/**
 * normalizeBackendRows
 * @description Convert various backend shapes into ShortlistRow[]
 * @param json any
 */
function normalizeBackendRows(json: any): { rows: ShortlistRow[]; total?: number } {
  if (!json) return { rows: [], total: undefined };

  if (Array.isArray(json)) {
    const rows = json.map((r: any) =>
      typeof r === 'string'
        ? { profileId: r }
        : {
            profileId: String(r.profileId ?? r.userId ?? r.id ?? ''),
            firstName: r.firstName ?? r.first_name ?? '',
            lastName: r.lastName ?? r.last_name ?? '',
            profession: r.profession ?? r.job ?? r.occupation ?? '',
            shortlistedAt: r.shortlistedAt ?? r.shortlisted_at ?? undefined,
            avatarUrl: r.avatarUrl ?? r.photoUrl ?? undefined,
          }
    );
    return { rows, total: rows.length };
  }

  if (json.data) return normalizeBackendRows(json.data);
  if (Array.isArray(json.shortlistedProfiles)) {
    const rows = json.shortlistedProfiles.map((r: any) => ({
      profileId: String(r.profileId ?? r.userId ?? r.id ?? ''),
      firstName: r.firstName ?? r.first_name ?? '',
      lastName: r.lastName ?? r.last_name ?? '',
      profession: r.profession ?? r.job ?? r.occupation ?? '',
      shortlistedAt: r.shortlistedAt ?? r.shortlisted_at ?? undefined,
      avatarUrl: r.avatarUrl ?? r.photoUrl ?? undefined,
    }));
    return { rows, total: typeof json.total === 'number' ? json.total : rows.length };
  }

  if (Array.isArray(json.rows)) {
    const rows = json.rows.map((r: any) => ({
      profileId: String(r.profileId ?? r.userId ?? r.id ?? ''),
      firstName: r.firstName ?? r.first_name ?? '',
      lastName: r.lastName ?? r.last_name ?? '',
      profession: r.profession ?? r.job ?? r.occupation ?? '',
      shortlistedAt: r.shortlistedAt ?? r.shortlisted_at ?? undefined,
      avatarUrl: r.avatarUrl ?? r.photoUrl ?? undefined,
    }));
    return { rows, total: typeof json.total === 'number' ? json.total : rows.length };
  }

  return { rows: [], total: undefined };
}

/**
 * resolveAbsoluteMediaUrl
 * @description Convert relative media paths to absolute using runtime apiBase()
 * @param pathOrUrl string
 */
function resolveAbsoluteMediaUrl(pathOrUrl?: string | null) {
  if (!pathOrUrl) return '';
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  try {
    const base = String(apiBase?.() ?? '').replace(/\/$/, '');
    if (!base) return pathOrUrl;
    const p = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
    return `${base}${p}`;
  } catch {
    return pathOrUrl;
  }
}

/**
 * Avatar
 * @description Compact avatar with initials fallback
 */
function Avatar({ src, alt, size = 40 }: { src?: string | null; alt?: string; size?: number }) {
  const [imgVisible, setImgVisible] = useState<boolean>(Boolean(src));
  useEffect(() => setImgVisible(Boolean(src)), [src]);
  const initials = (alt || '').split(' ').map(s => s?.[0] ?? '').join('').slice(0, 2).toUpperCase();

  return (
    <div
      className="relative flex-shrink-0 rounded-full overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center"
      style={{ width: size, height: size }}
      aria-hidden
    >
      {imgVisible && src ? (
        <img
          src={src}
          alt={alt ?? 'avatar'}
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
            setImgVisible(false);
          }}
        />
      ) : null}
      {!imgVisible && <span className="text-slate-700 font-semibold">{initials || '—'}</span>}
    </div>
  );
}

/**
 * ShortlistModal
 *
 * @description Compact modal listing shortlisted profiles in a 3x2 grid.
 * - Page size: 6 (3 columns x 2 rows)
 * - Modal background: light pink tone (bg-rose-50)
 * - Profile cards: thicker pink borders to create a clear separation between items
 */
export default function ShortlistModal({ isOpen, onClose, profileId }: ShortlistModalProps) {
  const navigate = useNavigate();

  // Data & UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<ShortlistRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState<number>(1);
  const PAGE_LIMIT = 6; // 3 x 2 grid
  const [total, setTotal] = useState<number | null>(null);

  // Caches & inflight tracking (in-memory)
  const avatarCacheRef = useRef<Map<string, string | null>>(new Map());
  const professionCacheRef = useRef<Map<string, string | null>>(new Map());
  const inflightAvatarsRef = useRef<Record<string, boolean>>({});
  const inflightProfRef = useRef<Record<string, boolean>>({});

  // Reset page when opening
  useEffect(() => {
    if (isOpen) setPage(1);
  }, [isOpen]);

  /**
   * load
   * @description Load shortlist list for current page
   */
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!isOpen) return;
      if (!profileId) {
        setError('Not logged in');
        setRows([]);
        setTotal(null);
        return;
      }
      setLoading(true);
      setError(null);

      try {
        const ep = `/shortlist/list/${encodeURIComponent(profileId)}?page=${page}&limit=${PAGE_LIMIT}`;
        const json = await fetchJSON(ep);
        const normalized = normalizeBackendRows(json);
        if (cancelled) return;
        setRows(normalized.rows);
        setTotal(typeof normalized.total === 'number' ? normalized.total : normalized.rows.length);

        // prefetch missing avatars/professions in background
        normalized.rows.forEach((r) => {
          if ((!r.avatarUrl || r.avatarUrl.trim() === '') && !avatarCacheRef.current.has(r.profileId) && !inflightAvatarsRef.current[r.profileId]) {
            fetchAvatarForProfile(r.profileId).catch(() => {});
          } else if (r.avatarUrl && !avatarCacheRef.current.has(r.profileId)) {
            avatarCacheRef.current.set(r.profileId, resolveAbsoluteMediaUrl(r.avatarUrl));
          }
          if ((!r.profession || String(r.profession).trim() === '') && !professionCacheRef.current.has(r.profileId) && !inflightProfRef.current[r.profileId]) {
            fetchProfessionForProfile(r.profileId).catch(() => {});
          } else if (r.profession && !professionCacheRef.current.has(r.profileId)) {
            professionCacheRef.current.set(r.profileId, String(r.profession));
          }
        });
      } catch (err: any) {
        // eslint-disable-next-line no-console
        console.error('[ShortlistModal] load error', err);
        if (!cancelled) {
          setError(String(err?.message ?? 'Failed to load shortlist'));
          setRows([]);
          setTotal(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, profileId, page]);

  /**
   * fetchAvatarForProfile
   * @description Try multiple endpoints to find a thumbnail; caches result
   */
  async function fetchAvatarForProfile(pid: string) {
    if (!pid) return null;
    if (avatarCacheRef.current.has(pid)) return avatarCacheRef.current.get(pid) ?? null;
    inflightAvatarsRef.current[pid] = true;
    try {
      // Try dashboard/user-photos
      try {
        const j = await fetchJSON(`/dashboard/user-photos/${encodeURIComponent(pid)}`);
        if (j && j.success && Array.isArray(j.photos) && j.photos.length > 0) {
          const p = j.photos[0];
          const cand = p.src || p.photoUrl || p.url || p.filePath || p.fileName;
          if (cand) {
            const resolved = resolveAbsoluteMediaUrl(cand);
            avatarCacheRef.current.set(pid, resolved);
            return resolved;
          }
        }
      } catch {
        // ignore
      }

      // Try folder listing
      try {
        const j = await fetchJSON(`/user-files/${encodeURIComponent(pid)}/Photos`);
        if (j && j.success && Array.isArray(j.files) && j.files.length > 0) {
          const f = j.files[0];
          const fileName = f.fileName || f.name;
          if (fileName) {
            const host = String(apiBase?.() ?? '').replace(/\/$/, '').replace(/\/api$/, '');
            const resolved = `${host}/Media/${pid}/Photos/${fileName}`;
            avatarCacheRef.current.set(pid, resolved);
            return resolved;
          }
        }
      } catch {
        // ignore
      }

      // Try users endpoint
      try {
        const ures = await fetchJSON(`/users/${encodeURIComponent(pid)}`);
        const u = ures?.data ?? ures;
        const candidates = [u?.avatarUrl, u?.photoUrl, u?.profilePhoto, u?.photo, u?.image];
        for (const cand of candidates) {
          if (cand) {
            const resolved = resolveAbsoluteMediaUrl(cand);
            avatarCacheRef.current.set(pid, resolved);
            return resolved;
          }
        }
      } catch {
        // ignore
      }

      avatarCacheRef.current.set(pid, null);
      return null;
    } finally {
      delete inflightAvatarsRef.current[pid];
    }
  }

  /**
   * fetchProfessionForProfile
   * @description Resolve profession from /users or /profiles and cache
   */
  async function fetchProfessionForProfile(pid: string) {
    console.log('[ShortlistModal] fetchProfessionForProfile called for:', pid);
    if (!pid) return null;
    if (professionCacheRef.current.has(pid)) {
      console.log('[ShortlistModal] Returning cached profession for:', pid);
      return professionCacheRef.current.get(pid) ?? null;
    }
    inflightProfRef.current[pid] = true;
    try {
      try {
        console.log('[ShortlistModal] Calling /users/' + pid);
        const res = await fetchJSON(`/users/${encodeURIComponent(pid)}`);
        console.log('[ShortlistModal] /users response:', res);
        const u = res?.user ?? res?.data ?? res;
        console.log('[ShortlistModal] Extracted user data:', u);
        const profVal = u && (u.profession ?? u.job ?? u.occupation);
        if (profVal && String(profVal).trim() !== '') {
          const prof = String(profVal);
          console.log('[ShortlistModal] Found profession:', prof);
          professionCacheRef.current.set(pid, prof);
          return prof;
        }
        console.log('[ShortlistModal] No profession in /users response');
      } catch (err) {
        console.log('[ShortlistModal] /users call failed:', err);
      }

      try {
        console.log('[ShortlistModal] Trying /profiles fallback for:', pid);
        const pres = await fetchJSON(`/profiles/user/${encodeURIComponent(pid)}?minimal=1`);
        console.log('[ShortlistModal] /profiles response:', pres);
        const p = pres?.profile ?? pres?.data ?? pres;
        console.log('[ShortlistModal] Extracted profile:', p);
        const profVal = p?.basicDetails?.profession ?? p?.basic?.profession ?? p?.profession ?? p?.personalDetails?.occupation ?? p?.personal?.occupation;
        if (profVal && String(profVal).trim() !== '') {
          const prof = String(profVal);
          console.log('[ShortlistModal] Found profession in profiles:', prof);
          professionCacheRef.current.set(pid, String(prof));
          return String(prof);
        }
        console.log('[ShortlistModal] No profession in /profiles response');
      } catch (err) {
        console.log('[ShortlistModal] /profiles call failed:', err);
      }

      console.log('[ShortlistModal] No profession found for:', pid);
      professionCacheRef.current.set(pid, null);
      return null;
    } finally {
      delete inflightProfRef.current[pid];
    }
  }

  function getCachedAvatarSrc(r: ShortlistRow): string | null {
    if (r.avatarUrl) return resolveAbsoluteMediaUrl(r.avatarUrl);
    const c = avatarCacheRef.current.get(r.profileId);
    if (typeof c === 'string') return c;
    return null;
  }

  function getCachedProfession(r: ShortlistRow): string {
    if (r.profession && String(r.profession).trim()) return String(r.profession);
    const p = professionCacheRef.current.get(r.profileId);
    return (p && String(p)) || '';
  }

  function handleViewProfile(pid: string) {
    onClose();
    navigate(`/search-profile/${encodeURIComponent(pid)}`);
  }

  /**
   * handleRemove
   * @description Optimistically remove and call delete endpoint; show toast with Undo
   */
  async function handleRemove(row: ShortlistRow) {
    if (!profileId) {
      toast.error('Not logged in');
      return;
    }

    const confirmRemoval = window.confirm(`Remove ${row.firstName || row.profileId} from your shortlist?`);
    if (!confirmRemoval) return;

    // optimistic update
    setRows((prev) => prev.filter((r) => r.profileId !== row.profileId));
    setTotal((t) => (t !== null ? Math.max(0, (t || 0) - 1) : null));
    setRemoving((s) => ({ ...s, [row.profileId]: true }));

    try {
      await fetchJSON(`/shortlist/remove/${encodeURIComponent(profileId)}/${encodeURIComponent(row.profileId)}`, { method: 'DELETE' });

      toast.success(
        <div className="flex items-center gap-2">
          <Check className="w-4 h-4 text-white" />
          <span>Removed from shortlist</span>
        </div>,
        {
          action: {
            label: 'Undo',
            onClick: async () => {
              try {
                await fetchJSON('/shortlist/add', { method: 'POST', body: { userId: profileId, profileId: row.profileId } });
                setRows((prev) => [row, ...prev]);
                setTotal((t) => (t !== null ? t + 1 : 1));
                toast(
                  <div className="flex items-center gap-2">
                    <RotateCcw className="w-4 h-4 text-white" /> Restored
                  </div>
                );
              } catch (err) {
                // eslint-disable-next-line no-console
                console.error('[ShortlistModal] undo failed', err);
                toast.error('Failed to undo');
              }
            },
          },
        }
      );
    } catch (err) {
      // revert UI on error
      // eslint-disable-next-line no-console
      console.error('[ShortlistModal] remove failed', err);
      setRows((prev) => [row, ...prev]);
      setTotal((t) => (t !== null ? t + 1 : 1));
      toast.error('Failed to remove from shortlist');
    } finally {
      setRemoving((s) => {
        const copy = { ...s };
        delete copy[row.profileId];
        return copy;
      });
    }
  }

  const totalPages = Math.max(1, Math.ceil((total ?? 0) / PAGE_LIMIT));

  if (!isOpen) return null;

  // Modal sizing tuned for 3x2 cards and larger horizontal space
  const modalMaxWidth = 'max-w-3xl'; // increased width as requested
  const modalMaxHeight = 'max-h-[560px]'; // tuned height to show two rows comfortably with added gaps

  return createPortal(
    <div className="fixed inset-0 z-[100000] flex items-center justify-center px-4 py-6 pointer-events-none">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/35 backdrop-blur-sm" onClick={onClose} />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Shortlisted profiles"
        className={`relative z-10 w-full ${modalMaxWidth} ${modalMaxHeight} bg-rose-50 rounded-lg shadow-lg overflow-hidden ring-1 ring-rose-100 pointer-events-auto`}
      >
        {/* Header - compact */}
        <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">Shortlisted Profiles</h3>
              <p className="text-xs opacity-90">Compact view — saved profiles</p>
            </div>
            <div className="ml-3 text-xs">
              <div className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                {total ?? '—'} Total
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-white/10 rounded-md p-1">
              <button aria-pressed={true} title="Grid view (3x2)" className="p-1 rounded bg-white/20">
                <Grid className="w-4 h-4 text-white" />
              </button>
            </div>

            <Button size="sm" className="bg-transparent text-white border-white/20" onClick={() => { setPage(1); }}>
              Refresh
            </Button>

            <button aria-label="Close" onClick={onClose} className="p-2 rounded hover:bg-white/10 text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content area: constrained grid (3 columns x 2 rows) */}
        <div className="p-3 overflow-auto" style={{ height: 'calc(100% - 112px)' }}>
          {loading ? (
            <div className="grid grid-cols-3 gap-x-4 gap-y-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse bg-white border-2 border-rose-100 rounded-lg p-3 h-36">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-200" />
                    <div className="flex-1">
                      <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
                      <div className="h-3 bg-slate-200 rounded w-1/2" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="py-6 text-center text-sm text-red-600">{error}</div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center mb-3">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <h4 className="text-sm font-semibold text-slate-800 mb-1">No profiles in your shortlist</h4>
              <p className="text-xs text-slate-500 mb-3">Start exploring and save profiles.</p>
              <div className="flex justify-center gap-2">
                <Button size="sm" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white" onClick={() => { onClose(); navigate('/search'); }}>
                  Find Matches
                </Button>
                <Button variant="outline" className="bg-transparent border-slate-200 text-slate-700" onClick={() => { onClose(); navigate('/profile'); }}>
                  View My Profile
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Grid: exactly 3 cols x up to 2 rows (6 items per page) */}
              <div className="grid grid-cols-3 grid-rows-2 gap-x-4 gap-y-6">
                {rows.map((r) => (
                  <article
                    key={r.profileId}
                    className="bg-white border-2 border-rose-200 rounded-lg p-3 h-36 flex flex-col justify-between hover:shadow-md hover:border-rose-300 transition"
                    aria-labelledby={`shortlist-title-${r.profileId}`}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar src={getCachedAvatarSrc(r)} alt={`${r.firstName || ''} ${r.lastName || ''}`} size={44} />
                      <div className="flex-1 min-w-0">
                        <div id={`shortlist-title-${r.profileId}`} className="font-medium text-sm truncate">
                          {r.firstName || '—'} {r.lastName || ''}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">{r.profileId}</div>
                        <div className="text-xs text-slate-500 mt-1 truncate">{getCachedProfession(r) || 'Profession not available'}</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-2 gap-2">
                      <div className="flex items-center gap-2">
                        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center" onClick={() => handleViewProfile(r.profileId)}>
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          className={removing[r.profileId] ? 'bg-gray-300 text-gray-700' : 'bg-red-600 hover:bg-red-700 text-white'}
                          onClick={() => handleRemove(r)}
                          disabled={!!removing[r.profileId]}
                        >
                          <Trash2 className="w-4 h-4 mr-1" /> {removing[r.profileId] ? 'Removing' : 'Remove'}
                        </Button>
                      </div>
                    </div>
                  </article>
                ))}
                {/* Fill remaining grid slots to keep consistent layout */}
                {Array.from({ length: Math.max(0, PAGE_LIMIT - rows.length) }).map((_, idx) => (
                  <div key={`empty-${idx}`} className="bg-transparent" />
                ))}
              </div>

              {/* Pagination */}
              <div className="mt-4 flex items-center justify-between text-xs text-slate-600">
                <div>Page {page} of {totalPages}</div>
                <div className="flex items-center gap-2">
                  <Button size="sm" className="bg-transparent text-slate-700" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                    <ChevronLeft className="w-4 h-4 mr-1" /> Prev
                  </Button>
                  <Button size="sm" className="bg-transparent text-slate-700" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end px-3 py-2 border-t">
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={onClose}>Done</Button>
        </div>
      </div>
    </div>,
    document.body
  );
}